import { FormHeader } from '@/interfaces/api/models';
import { FormFieldValue } from './form-helpers';

export interface FormDraftSnapshot {
    formDefinitionID: string;
    formHeaderID: string;
    /**
     * Keyed by `buildAutosaveComposite(item)` — `FormDefinitionFieldID:SectionGroup:ParentSectionGroup`
     * — NOT by `FormItem.ID`.
     *
     * A never-saved form regenerates every item GUID on each mount, so item IDs written into a
     * snapshot before a crash mean nothing to the mount that reads it back; the composite is
     * reproduced identically. `useFormAutosave.restoreValues` maps these keys back onto the current
     * session's real item IDs.
     */
    values: Record<string, FormFieldValue>;
    savedAt: string;
    sessionOpen: boolean;
}

export function computeDraftKey(formHeader: FormHeader, isNew: boolean): string {
    if (!isNew) {
        return `formDraft:${formHeader.ID}`;
    }
    return `formDraft:new:${formHeader.FormDefinitionID}:${formHeader.ItemID}`;
}

export function readDraft(key: string): FormDraftSnapshot | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw) as FormDraftSnapshot;
    } catch {
        return null;
    }
}

export function writeDraft(key: string, snapshot: FormDraftSnapshot): void {
    try {
        localStorage.setItem(key, JSON.stringify(snapshot));
    } catch {
        // localStorage can throw on quota errors — autosave failing silently is preferable to crashing the form
    }
}

export function deleteDraft(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // no-op
    }
}
